import { html, View } from 'rune-ts';
import { CreatorTypeS } from '@services/MarppleShop/features/Creator/outbound/share.ts';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle.ts';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import { font, staticFont } from '@services/MarppleShop/shared/typography';
import { BaseCreatorRankerCard } from '@services/MarppleShop/renderApp/components/cells/CreatorRanking/BaseCreatorRankerCard.ts';
import klass from './NewCreatorRanking.module.scss';
import { chunk, pipe, map, toArray } from '@fxts/core';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { makeEmptyScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { makePlusUrl } from '@services/MarppleShop/shared/util/url.ts';
import { getSharedData } from '@services/MarppleShop/shared/util/rune.ts';
import { isAllCrewById } from '@services/MarppleShop/shared/util';

export interface NewCreatorRankingSectionProps {
  creator_rankings: CreatorTypeS.ICreatorRanking[];
  is_mobile?: boolean;
}

export interface NewCreatorRankingCardProps {
  creator_ranking: CreatorTypeS.ICreatorRanking;
  is_mobile?: boolean;
}

export class NewCreatorRankingCard extends BaseCreatorRankerCard<NewCreatorRankingCardProps> {
  protected override template() {
    const sharedData = getSharedData(this);

    return this.createContainerWithBackdrop(
      html`
        <a
          href="/${ET.lang}/${this.data.creator_ranking.domain_name}"
          data-post-message="${dataStr(
            makeEmptyScreenNavigate(
              sharedData?.plus_crew && isAllCrewById(sharedData.plus_crew.id)
                ? `/${ET.lang}/${this.data.creator_ranking.domain_name}`
                : makePlusUrl(
                    sharedData?.plus_crew?.domain_name,
                    `/${ET.lang}/${this.data.creator_ranking.domain_name}`,
                  ),
            ),
          )}"
          class="${klass.new_creator_ranking_card_wrapper}"
        >
          <!--  크리에이터 인포  -->
          ${this.makeCreatorInfo()}

          <!--  크리에이터 베스트 상품 리스트  -->
          <div class="${klass.new_creator_ranking_best_product_list}">
            ${this.makeProductImages(klass.new_creator_ranking_best_product_item)}
          </div>
        </a>
      `,
      klass.new_creator_ranking_card,
    );
  }

  private makeCreatorInfo() {
    return html`
      <div class="${klass.new_creator_ranking_card_info}">
        <h3 class="${staticFont('bebas_48_bold', 'bebas_32_bold')}">
          TOP ${this.data.creator_ranking.current_rank}
        </h3>
        <div>
          <div class="${klass.new_creator_ranking_card_name} ${font('28_bold', '20_bold')}">
            <span>${this.data.creator_ranking.name}</span><i>${this.mShopDefaultIcon}</i>
          </div>
          ${this.data.creator_ranking.seller_type_name
            ? html`<span class="${font('14_medium')}"> ${this.data.creator_ranking.seller_type_name} </span>`
            : ''}
        </div>
      </div>
    `;
  }
}

export class NewCreatorRankingSection extends View<NewCreatorRankingSectionProps> {
  protected override template() {
    return html`
      <div class="${klass.new_creator_ranking_section}">
        <!-- 섹션 타이틀 -->
        <div class="${klass.section_title}">${this.makeSectionTitle()}</div>

        <!-- 신규 크리에이터 카드 -->
        ${this.makeNewCreatorRankingList()}
      </div>
    `;
  }

  private makeSectionTitle() {
    return new SectionTitle({
      title_props: {
        title: ET('mps2::main::new_creator_ranking::title'),
        sub_title: ET('mps2::main::new_creator_ranking::sub_title'),
        size: this.data.is_mobile ? 'large' : 'xLarge',
        tooltip: new Tooltip({
          id: 'NewCreatorRankingSectionTooltip',
          size: 'medium',
          content: html`
            <div class="${klass.tooltip}">
              ${html.preventEscape(UtilS.escape(ET('mps2::main::new_creator_ranking::tooltip'), true))}
            </div>
          `,
        }),
      },
    });
  }

  private makeNewCreatorRankingList() {
    if (this.data.is_mobile)
      return html`
        <ul class="${klass.new_creator_ranking_list}">
          ${pipe(
            this.data.creator_rankings,
            chunk(2),
            map(
              (slice) =>
                html`<div class="${klass.new_creator_ranking_list_slice}">
                  ${slice.map(
                    (creator_ranking) =>
                      new NewCreatorRankingCard({
                        creator_ranking,
                      }),
                  )}
                </div> `,
            ),
            toArray,
          )}
        </ul>
      `;

    return html`
      <ul class="${klass.new_creator_ranking_list}">
        ${this.data.creator_rankings.map(
          (creator_ranking) =>
            new NewCreatorRankingCard({
              creator_ranking,
            }),
        )}
      </ul>
    `;
  }
}
